import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUserProfile, memToFirestore } from "@/contexts/UserProfileContext";
import { useAuth } from "@/contexts/AuthContext";
import VitalsMeasurement from "@/components/we360/VitalsMeasurement";
import WowResultsStep from "./Onboarding/WowResultsStep";
import { saveMeasurement, syncMetricsToGCP } from "@/services/measurements";
import { syncWebUser, COUNTRY_CODES, normalizeWhatsAppPhone, validateWhatsAppPhone, splitWhatsAppPhone } from "@/services/coreServicesClient";
import { syncF1 } from "@/services/backendSync";
import { getSavedB2BInvite, fetchCompanyName } from "@/services/b2bInviteService";
import { calculateIMC, classifyIMC } from "@/pages/FormF1/helpers";
import { db } from "@/firebase";
import { doc, setDoc, collection, query, where, getDocs } from "firebase/firestore";
import { 
  Camera, Sparkles, Lock, Building2, Heart, 
  Activity, Zap, ArrowRight, ArrowLeft, User, Calendar, Phone, CheckCircle2
} from "lucide-react";

export { COUNTRY_CODES, normalizeWhatsAppPhone, validateWhatsAppPhone, splitWhatsAppPhone };

async function isPhoneLinkedToAnotherAccount(cleanPhone, currentUid) {
  if (!cleanPhone || !currentUid) return false;
  try {
    const usersRef = collection(db, "users");
    const q1 = query(usersRef, where("whatsapp", "in", [cleanPhone, `+${cleanPhone}`]));
    const snap = await getDocs(q1);
    return snap.docs.some((d) => d.id !== currentUid);
  } catch (err) {
    console.debug("[Onboarding] No se pudo verificar duplicidad de WhatsApp:", err.message);
    return false;
  }
}

export default function Onboarding() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    profile, 
    setProfile, 
    completeOnboarding, 
    onboardingCompleted, 
    clientId, 
    isDemo, 
    isImpersonating 
  } = useUserProfile();

  // Estados del flujo: "welcome" | "calibrate" | "scan" | "wow"
  const [stage, setStage] = useState("welcome");
  const [showCameraModal, setShowCameraModal] = useState(false);
  const [scanResults, setScanResults] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Datos biométricos basales para calibración de rPPG
  const [nombres, setNombres] = useState(
    profile?.nombres || user?.displayName?.split(" ")?.[0] || ""
  );
  const [apellidos, setApellidos] = useState(
    profile?.apellidos || user?.displayName?.split(" ")?.slice(1)?.join(" ") || ""
  );
  const [sexo, setSexo] = useState(profile?.sexo || "");
  const [fechaNacimiento, setFechaNacimiento] = useState(profile?.fechaNacimiento || "");
  const [talla, setTalla] = useState(profile?.talla ? String(profile.talla) : "170");
  const [peso, setPeso] = useState(profile?.peso ? String(profile.peso) : "70");
  const initialPhoneSplit = splitWhatsAppPhone(
    profile?.whatsapp || profile?.telefono || user?.phoneNumber || "",
    profile?.countryCode || "+507"
  );
  const [countryCode, setCountryCode] = useState(initialPhoneSplit.countryCode);
  const [whatsapp, setWhatsapp] = useState(initialPhoneSplit.localNumber);

  useEffect(() => {
    const rawFromProfile = profile?.whatsapp || profile?.telefono || user?.phoneNumber || "";
    if (!whatsapp && rawFromProfile) {
      const split = splitWhatsAppPhone(rawFromProfile, profile?.countryCode || "+507");
      setCountryCode(split.countryCode);
      setWhatsapp(split.localNumber);
    }
  }, [profile?.whatsapp, profile?.telefono, profile?.countryCode, user?.phoneNumber, whatsapp]);

  // Redirigir si el onboarding ya fue completado previamente
  useEffect(() => {
    if (onboardingCompleted) {
      navigate("/dashboard", { replace: true });
    }
  }, [onboardingCompleted, navigate]);

  // Cargar nombre de empresa aliada B2B si aplica
  useEffect(() => {
    let active = true;
    async function loadCompany() {
      if (profile?.empresa) {
        if (active) setCompanyName(profile.empresa);
        return;
      }
      const saved = getSavedB2BInvite();
      const targetCid = clientId || saved.cid;
      if (targetCid) {
        const name = await fetchCompanyName(targetCid);
        if (active && name) setCompanyName(name);
      }
    }
    loadCompany();
    return () => { active = false; };
  }, [profile?.empresa, clientId]);

  // Cálculo en caliente de la edad cronológica real
  const calculatedAge = useMemo(() => {
    if (!fechaNacimiento) return 35;
    const d = new Date(fechaNacimiento);
    if (isNaN(d.getTime())) return 35;
    const diffMs = Date.now() - d.getTime();
    const age = Math.floor(diffMs / (365.25 * 24 * 60 * 60 * 1000));
    return age >= 10 && age <= 120 ? age : 35;
  }, [fechaNacimiento]);

  // Cálculo de IMC en tiempo real para la pantalla de calibración
  const calibrationImc = useMemo(() => {
    const p = parseFloat(peso);
    const t = parseFloat(talla);
    if (!p || !t || p <= 0 || t <= 0) return null;
    const imcVal = calculateIMC(p, t);
    if (!imcVal) return null;
    const cl = classifyIMC(parseFloat(imcVal));
    return {
      value: imcVal,
      label: cl?.label || "Normal",
      color: cl?.color || "#157346"
    };
  }, [peso, talla]);

  // Iniciar calibración previa al escaneo
  const handleGoToCalibrate = () => {
    setError(null);
    setStage("calibrate");
  };

  // Validar calibración y abrir cámara con parámetros reales
  const handleStartScan = async () => {
    setError(null);

    const p = parseFloat(peso);
    const t = parseFloat(talla);
    const phoneValidation = validateWhatsAppPhone(whatsapp, countryCode);

    if (!sexo) {
      setError("Por favor selecciona tu sexo biológico para calibrar los algoritmos cardiovasculares.");
      return;
    }
    if (!fechaNacimiento) {
      setError("Por favor ingresa tu fecha de nacimiento para comparar tu edad vascular con tu edad cronológica.");
      return;
    }
    if (!p || p < 30 || p > 250) {
      setError("Por favor ingresa un peso válido (30 a 250 kg).");
      return;
    }
    if (!t || t < 100 || t > 230) {
      setError("Por favor ingresa una estatura válida (100 a 230 cm).");
      return;
    }
    if (!phoneValidation.valid) {
      setError(phoneValidation.error || "Por favor ingresa tu número de WhatsApp válido para vincular tu Coach de Salud IA.");
      return;
    }

    if (user?.uid && !isDemo && !isImpersonating) {
      const duplicated = await isPhoneLinkedToAnotherAccount(phoneValidation.normalized, user.uid);
      if (duplicated) {
        setError("Este número de WhatsApp ya está vinculado a otra cuenta en WE360. Por favor verifica tu número o ingresa uno diferente.");
        return;
      }
    }

    setShowCameraModal(true);
    setStage("scan");
  };

  // Cancelar o cerrar el escaneo
  const handleCancelScan = () => {
    setShowCameraModal(false);
    setStage("calibrate");
  };

  // Al completar los 30s de escaneo rPPG con éxito
  const handleScanComplete = (results) => {
    console.log("[Onboarding] 📊 Resultados de escaneo rPPG recibidos:", results);
    setScanResults(results);
    setShowCameraModal(false);
    setStage("wow");
  };

  // Finalizar Onboarding desde la pantalla de revelación Wow
  const handleWowFinish = async ({ peso: finalPeso, talla: finalTalla, bmi, whatsapp: finalWhatsapp, results }) => {
    setSaving(true);
    setError(null);

    try {
      const p = parseFloat(finalPeso || peso) || null;
      const t = parseFloat(finalTalla || talla) || null;
      const finalBmi = bmi || (p && t ? calculateIMC(p, t) : null);
      const rawPhoneCandidate = finalWhatsapp || whatsapp || profile?.whatsapp || profile?.telefono || user?.phoneNumber || "";
      const phoneCheck = validateWhatsAppPhone(rawPhoneCandidate, countryCode);
      const cleanPhone = phoneCheck.normalized || normalizeWhatsAppPhone(rawPhoneCandidate, countryCode);

      if (user?.uid && !isDemo && !isImpersonating && cleanPhone) {
        const duplicated = await isPhoneLinkedToAnotherAccount(cleanPhone, user.uid);
        if (duplicated) {
          setError("Este número de WhatsApp ya está vinculado a otra cuenta en WE360.");
          setSaving(false);
          return;
        }
      }

      const fieldsToSave = {
        paSistolica: results?.bloodPressureSystolic || null,
        paDiastolica: results?.bloodPressureDiastolic || null,
        oximetria: results?.oxygenSaturation || null,
        heartRate: results?.heartRate || null,
        heartRateVariability: results?.heartRateVariability || null,
        respirationRate: results?.respirationRate || null,
        stressIndex: results?.stressIndex != null ? Math.round(results.stressIndex) : null,
        cardiovascularAge: results?.cardiovascularAge != null ? Math.round(results.cardiovascularAge) : null,
        cardiovascularBMI: results?.cardiovascularBMI || null,
        cvdRisk: results?.cvdRisk || null,
        wellnessScore: results?.wellnessScore || null,
        ansIndexPNS: results?.ansIndexPNS || null,
        ansIndexSNS: results?.ansIndexSNS || null,
        hba1c: results?.hba1cValue ?? results?.hba1c ?? null,
        hemoglobin: results?.hemoglobin ?? null,
        cholesterol: results?.cholesterolValue ?? results?.cholesterol ?? null,
        shockIndex: results?.shockIndex ?? null,
        prq: results?.prq ?? null,
        hrvRMSSD: results?.hrvRMSSD ?? null,
        hrvSDNN: results?.hrvSDNN ?? null,
        ...(p ? { peso: p } : {}),
        ...(t ? { talla: t } : {}),
      };

      if (user && !isDemo && !isImpersonating) {
        // 1. Sincronizar usuario a Leandro / Core Services (crea Patient en FHIR Store de DEV y vincula WhatsApp Bot)
        const fhirGender = sexo === "Femenino" ? "female" : "male";
        let fhirId = null;

        if (cleanPhone) {
          try {
            const syncRes = await syncWebUser({
              users: [
                {
                  email: user.email,
                  phone: cleanPhone,
                  firstName: nombres.trim() || user?.displayName?.split(" ")?.[0] || "Usuario",
                  lastName: apellidos.trim() || user?.displayName?.split(" ")?.slice(1)?.join(" ") || "WE360",
                  birthDate: fechaNacimiento || "1990-01-01",
                  gender: fhirGender,
                  weightKg: p || 70,
                  heightCm: t || 170,
                }
              ]
            });
            fhirId = syncRes?.results?.[0]?.fhirId || null;
            console.log("[Onboarding] ✅ Usuario sincronizado en Core Services FHIR:", fhirId);
          } catch (syncErr) {
            console.warn("[Onboarding] Aviso: Error registrando en Core Services:", syncErr.message);
          }
        }

        // 2. Guardar medición inmutable en users/{uid}/measurements (Outbox inicial)
        let savedMeasurementId = null;
        try {
          const savedMeas = await saveMeasurement(user.uid, {
            method: "camera",
            fields: fieldsToSave,
            note: "Medición basal de bienvenida (Scan-First Onboarding)"
          });
          savedMeasurementId = savedMeas?.id || null;
        } catch (measErr) {
          console.warn("[Onboarding] Error registrando medición inmutable:", measErr);
        }

        // 3. Sincronizar observaciones LOINC a GCP / FHIR Store de Leandro con tracking Outbox
        syncMetricsToGCP(user.email || profile?.email, fieldsToSave, {
          uid: user.uid,
          measurementId: savedMeasurementId,
        }).catch((e) => {
          console.warn("[Onboarding] syncMetricsToGCP aviso:", e);
        });

        // 4. Construir objeto FHIR Patient para Firestore users/{uid}.profile
        const updatedMemProfile = {
          ...profile,
          uid: user.uid,
          email: user.email || profile.email,
          nombres: nombres.trim(),
          apellidos: apellidos.trim(),
          fechaNacimiento,
          sexo,
          whatsapp: cleanPhone || whatsapp || profile.whatsapp || "",
          telefono: cleanPhone || whatsapp || profile.telefono || "",
          peso: p,
          talla: t,
          imc: finalBmi ? String(finalBmi) : null,
          ...fieldsToSave,
          vitalsMeasurementDate: new Date().toISOString(),
          vitalsMeasurementMethod: "camera",
          fhirId: fhirId || profile.fhirId || null,
          fhirSyncStatus: "PENDING_FHIR",
          pendingFhirF1: true,
        };

        const fhirDoc = memToFirestore(updatedMemProfile);

        // 5. Actualizar documento users/{uid} en Firestore (con flag Outbox pendingFhirF1: true hasta que syncF1 confirme)
        const userRef = doc(db, "users", user.uid);
        const updatePayload = {
          onboardingCompleted: true,
          onboardingStatus: "completed",
          onboardingStep: "completed",
          onboardingCompletedAt: new Date().toISOString(),
          fhirSyncStatus: "PENDING_FHIR",
          pendingFhirF1: true,
          ...(cleanPhone ? { whatsapp: cleanPhone, telefono: cleanPhone } : {}),
          ...(p ? { peso: String(p) } : {}),
          ...(t ? { talla: String(t) } : {}),
          ...(finalBmi ? { imc: String(finalBmi) } : {}),
          ...(fhirId ? { fhirId } : {}),
          ...(fieldsToSave.heartRate ? { heartRate: fieldsToSave.heartRate } : {}),
          ...(fieldsToSave.oximetria ? { oximetria: fieldsToSave.oximetria } : {}),
          ...(fieldsToSave.stressIndex ? { stressIndex: fieldsToSave.stressIndex } : {}),
          ...(fieldsToSave.cardiovascularAge ? { cardiovascularAge: fieldsToSave.cardiovascularAge } : {}),
          profile: fhirDoc,
        };

        await setDoc(userRef, updatePayload, { merge: true });

        // Sincronizar en memoria React
        setProfile(updatedMemProfile);

        // 6. Sincronizar F1 a GCP FHIR (Crea el Patient y observaciones basales en FHIR Store de Google Cloud y marca SYNCED)
        syncF1(updatedMemProfile, user.email).catch((e) => {
          console.warn("[Onboarding] syncF1 aviso:", e);
        });
      }

      await completeOnboarding();
      navigate("/dashboard", {
        replace: true,
        state: { measurementSaved: true, method: "camera", isWelcomeScan: true }
      });
    } catch (err) {
      console.error("[Onboarding] Error al finalizar onboarding:", err);
      setError("Ocurrió un error al guardar tus datos. Puedes continuar al tablero directamente.");
      setSaving(false);
    }
  };

  // Omitir escaneo e ir directamente al dashboard
  const handleSkip = async () => {
    await completeOnboarding();
    navigate("/dashboard", { replace: true });
  };

  return (
    <div
      className="min-h-screen font-sans flex flex-col justify-center items-center px-4 py-8 relative overflow-hidden"
      style={{ background: "#FCFAEE" }}
      data-testid="onboarding-screen"
    >
      {/* Glow ambiental decorativo */}
      <div 
        className="absolute top-0 left-0 w-[500px] h-[500px] rounded-full pointer-events-none filter blur-[120px] mix-blend-multiply opacity-60" 
        style={{ background: "rgba(42, 157, 143, 0.08)" }} 
      />
      <div 
        className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full pointer-events-none filter blur-[100px] mix-blend-multiply opacity-60" 
        style={{ background: "rgba(0, 98, 143, 0.08)" }} 
      />

      {/* ═══ ETAPA 1: BIENVENIDA & PROPUESTA DE VALOR ═══ */}
      {stage === "welcome" && (
        <div className="w-full max-w-md mx-auto z-10 text-center animate-fadeIn" data-testid="onboarding-welcome">
          {/* Badge de Empresa B2B o WE360 */}
          {companyName ? (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-teal-50 text-teal-800 border border-teal-200/80 shadow-sm mb-4">
              <Building2 size={13} className="text-teal-600" />
              <span>Bienvenido al programa de salud de <strong>{companyName}</strong></span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[#00628F]/10 text-[#00628F] mb-4">
              <Sparkles size={13} className="text-[#00628F]" />
              <span>Bienvenido a WE 360</span>
            </div>
          )}

          {/* Logo / Ícono Central */}
          <div className="w-20 h-20 mx-auto rounded-3xl flex items-center justify-center bg-gradient-to-br from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-xl shadow-[#00628F]/25 mb-6 transform hover:scale-105 transition-transform duration-300">
            <Camera size={38} color="white" />
          </div>

          <h1 
            className="text-2xl sm:text-3xl font-extrabold text-[#2D2A24] leading-tight mb-3"
            data-testid="onboarding-welcome-title"
          >
            Tu chequeo biométrico instantáneo en 30 segundos
          </h1>

          <p className="text-sm text-[#71644C] leading-relaxed mb-6 font-medium">
            {companyName
              ? `Como parte de tu programa de bienestar corporativo, accede a un chequeo instantáneo de signos vitales y edad vascular sin pinchazos ni equipos invasivos.`
              : `Descubre tu edad cardiovascular estimada, nivel de estrés y signos vitales mirando a la cámara durante 30 segundos.`}
          </p>

          {/* Beneficios clave en tarjeta glassmorphism */}
          <div className="bg-white/80 backdrop-blur-md rounded-2xl p-4 shadow-sm border border-[#E2D9CB]/80 mb-6 text-left space-y-3">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-lg bg-teal-500/10 flex items-center justify-center text-teal-700 shrink-0 mt-0.5">
                <Zap size={14} />
              </div>
              <div>
                <p className="text-xs font-bold text-[#2D2A24]">Chequeo Óptico sin Contacto</p>
                <p className="text-[11px] text-[#71644C]">Mide tus signos vitales y pulso a través de la cámara, sin cables ni sensores invasivos.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-lg bg-[#00628F]/10 flex items-center justify-center text-[#00628F] shrink-0 mt-0.5">
                <Heart size={14} />
              </div>
              <div>
                <p className="text-xs font-bold text-[#2D2A24]">Edad Cardiovascular y Nivel de Estrés</p>
                <p className="text-[11px] text-[#71644C]">Descubre si tu sistema circulatorio es más joven que tu edad cronológica y evalúa tu equilibrio ante la tensión.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-lg bg-emerald-500/10 flex items-center justify-center text-emerald-700 shrink-0 mt-0.5">
                <Lock size={14} />
              </div>
              <div>
                <p className="text-xs font-bold text-[#2D2A24]">100% Seguro y Privado</p>
                <p className="text-[11px] text-[#71644C]">Procesamiento en tiempo real en tu dispositivo. Tu cámara nunca graba ni almacena imágenes o videos.</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 border border-red-200 text-xs text-center font-medium">
              {error}
            </div>
          )}

          {/* CTA Principal */}
          <button
            type="button"
            onClick={handleGoToCalibrate}
            className="w-full cursor-pointer py-4 px-6 rounded-2xl bg-gradient-to-r from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-lg shadow-[#00628F]/25 hover:shadow-xl hover:scale-[1.01] active:scale-[0.98] transition-all text-white font-bold text-base flex items-center justify-center gap-2 border-none mb-3"
            data-testid="onboarding-start-scan-btn"
          >
            <span>Comenzar Chequeo Biométrico</span>
            <ArrowRight size={18} />
          </button>

          {/* Omitir */}
          <button
            type="button"
            onClick={handleSkip}
            className="text-xs font-semibold text-[#71644C] hover:text-[#00628F] transition-colors"
            data-testid="onboarding-skip-btn"
          >
            Omitir por ahora e ir directamente al tablero
          </button>

          {/* Legal footer */}
          <p className="text-[10px] text-[#B5A998] mt-6 leading-relaxed">
            Al continuar aceptas nuestro{" "}
            <a href="https://saluddigital360.com/acuerdo-usuario" target="_blank" rel="noopener noreferrer" className="underline text-[#71644C]">
              acuerdo de usuario
            </a>{" "}
            y{" "}
            <a href="https://saluddigital360.com/politicas-privacidad" target="_blank" rel="noopener noreferrer" className="underline text-[#71644C]">
              política de privacidad
            </a>.
          </p>
        </div>
      )}

      {/* ═══ ETAPA 1.5: CALIBRACIÓN BIOMÉTRICA PREVIA AL ESCANEO ═══ */}
      {stage === "calibrate" && (
        <div className="w-full max-w-lg mx-auto z-10 animate-fadeIn" data-testid="onboarding-calibrate">
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold text-teal-700 bg-teal-500/10 mb-2">
              <Activity size={14} className="text-teal-600" />
              <span>Calibración Previa al Escaneo</span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#2D2A24] tracking-tight">
              Calibra tu chequeo con la cámara
            </h1>
            <p className="text-xs sm:text-sm text-[#71644C] mt-2 max-w-md mx-auto leading-relaxed">
              Para que la inteligencia artificial estime con máxima exactitud tu <strong>presión arterial</strong> y compare tu <strong>edad cardiovascular</strong> con tu edad cronológica real, confirma estos datos básicos:
            </p>
          </div>

          <div className="bg-white/90 backdrop-blur-md rounded-3xl p-6 sm:p-7 shadow-xl border border-[#E2D9CB]/80 mb-6">
            {/* Nombre y Apellidos */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-4">
              <div>
                <label className="block text-xs font-bold text-[#2D2A24] mb-1">
                  Nombre(s)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={nombres}
                    onChange={(e) => setNombres(e.target.value)}
                    placeholder="Ej. Carlos"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F] transition-all"
                    data-testid="calibrate-nombres"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#2D2A24] mb-1">
                  Apellido(s)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={apellidos}
                    onChange={(e) => setApellidos(e.target.value)}
                    placeholder="Ej. Gómez"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F] transition-all"
                    data-testid="calibrate-apellidos"
                  />
                </div>
              </div>
            </div>

            {/* Sexo Biológico y Fecha de Nacimiento */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-4">
              <div>
                <label className="block text-xs font-bold text-[#2D2A24] mb-1">
                  Sexo biológico (al nacer)
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setSexo("Femenino")}
                    className={`py-2.5 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border transition-all ${
                      sexo === "Femenino"
                        ? "bg-[#00628F] text-white border-[#00628F] shadow-sm"
                        : "bg-white text-[#71644C] border-[#E2D9CB] hover:border-[#00628F]"
                    }`}
                    data-testid="calibrate-sexo-femenino"
                  >
                    <span>Femenino</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSexo("Masculino")}
                    className={`py-2.5 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-1.5 border transition-all ${
                      sexo === "Masculino"
                        ? "bg-[#00628F] text-white border-[#00628F] shadow-sm"
                        : "bg-white text-[#71644C] border-[#E2D9CB] hover:border-[#00628F]"
                    }`}
                    data-testid="calibrate-sexo-masculino"
                  >
                    <span>Masculino</span>
                  </button>
                </div>
                <p
                  className="text-[10.5px] text-[#71644C] mt-1 leading-tight"
                  data-testid="calibrate-sexo-hint"
                >
                  Requerido exclusivamente para calibrar tus rangos fisiológicos y cardiovasculares.
                </p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-bold text-[#2D2A24]">
                    Fecha de nacimiento
                  </label>
                  {fechaNacimiento && calculatedAge && (
                    <span className="text-[11px] font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200/60">
                      {calculatedAge} años
                    </span>
                  )}
                </div>
                <div className="relative">
                  <input
                    type="date"
                    value={fechaNacimiento}
                    onChange={(e) => setFechaNacimiento(e.target.value)}
                    max={new Date().toISOString().split("T")[0]}
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F] transition-all"
                    data-testid="calibrate-fecha-nacimiento"
                  />
                </div>
              </div>
            </div>

            {/* Estatura y Peso */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-4">
              <div>
                <label className="block text-xs font-bold text-[#2D2A24] mb-1">
                  Estatura (cm)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="1"
                    min="100"
                    max="230"
                    value={talla}
                    onChange={(e) => setTalla(e.target.value)}
                    placeholder="170"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F] transition-all"
                    data-testid="calibrate-talla"
                  />
                  <span className="absolute right-3.5 top-2.5 text-xs font-bold text-[#71644C]">
                    cm
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-[#2D2A24] mb-1">
                  Peso actual (kg)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    step="0.1"
                    min="30"
                    max="250"
                    value={peso}
                    onChange={(e) => setPeso(e.target.value)}
                    placeholder="70.0"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F] transition-all"
                    data-testid="calibrate-peso"
                  />
                  <span className="absolute right-3.5 top-2.5 text-xs font-bold text-[#71644C]">
                    kg
                  </span>
                </div>
              </div>
            </div>

            {/* Badge interactivo de IMC */}
            {calibrationImc && (
              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-between mb-4 animate-fadeIn">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-700">IMC estimado:</span>
                  <span className="text-sm font-black text-slate-900">{calibrationImc.value} kg/m²</span>
                </div>
                <span
                  className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                  style={{
                    backgroundColor: `${calibrationImc.color}20`,
                    color: calibrationImc.color
                  }}
                  data-testid="calibrate-imc-badge"
                >
                  {calibrationImc.label}
                </span>
              </div>
            )}

            {/* WhatsApp (requerido para vincular el Coach de Salud IA en WhatsApp) */}
            <div>
              <label className="block text-xs font-bold text-[#2D2A24] mb-1">
                WhatsApp para tu Coach de Salud IA <span className="text-teal-700">*</span>
              </label>
              <div className="flex gap-2">
                <select
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="px-2.5 py-2.5 rounded-xl border border-[#E2D9CB] bg-white text-xs font-bold text-[#2D2A24] focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F] transition-all shrink-0"
                  data-testid="calibrate-country-code"
                  aria-label="Código de país"
                >
                  {COUNTRY_CODES.map((cc) => (
                    <option key={cc.code} value={cc.code}>
                      {cc.label}
                    </option>
                  ))}
                </select>
                <div className="relative flex-1">
                  <input
                    type="tel"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    placeholder="6000-0000"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-[#E2D9CB] bg-white text-sm font-semibold text-[#2D2A24] focus:outline-none focus:ring-2 focus:ring-[#00628F]/20 focus:border-[#00628F] transition-all"
                    data-testid="calibrate-whatsapp"
                  />
                </div>
              </div>
              <p className="text-[11px] text-[#71644C] mt-1">
                Indispensable para asociar tus resultados al asistente clínico en WhatsApp y recibir seguimiento diario.
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 text-red-700 border border-red-200 text-xs text-center font-medium animate-fadeIn">
              {error}
            </div>
          )}

          {/* Botones de acción */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleStartScan}
              className="w-full cursor-pointer py-4 px-6 rounded-2xl bg-gradient-to-r from-[#00628F] via-[#0080A0] to-[#2A9D8F] shadow-lg shadow-[#00628F]/25 hover:shadow-xl hover:scale-[1.01] active:scale-[0.98] transition-all text-white font-bold text-base flex items-center justify-center gap-2 border-none"
              data-testid="calibrate-submit-btn"
            >
              <Camera size={18} />
              <span>Abrir Cámara e Iniciar Chequeo (30s)</span>
            </button>

            <div className="flex items-center justify-between pt-1">
              <button
                type="button"
                onClick={() => setStage("welcome")}
                className="text-xs font-semibold text-[#71644C] hover:text-[#00628F] flex items-center gap-1 transition-colors"
              >
                <ArrowLeft size={14} />
                <span>Volver</span>
              </button>

              <button
                type="button"
                onClick={handleSkip}
                className="text-xs font-semibold text-[#71644C] hover:text-[#00628F] transition-colors"
              >
                Omitir escaneo e ir al tablero
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ ETAPA 2: ESCANEO FACIAL RPPG INTEGRADO ═══ */}
      <VitalsMeasurement
        isOpen={showCameraModal}
        onClose={handleCancelScan}
        onComplete={handleScanComplete}
        userInfo={{
          age: calculatedAge,
          sex: sexo === "Femenino" ? 0 : 1,
          height: parseFloat(talla) || 170,
          weight: parseFloat(peso) || 70,
          bp_group: "normal"
        }}
      />

      {/* ═══ ETAPA 3: FACTOR WOW (REVELACIÓN Y CONFIRMACIÓN BASAL) ═══ */}
      {stage === "wow" && scanResults && (
        <WowResultsStep
          results={scanResults}
          userAge={calculatedAge}
          initialPeso={peso}
          initialTalla={talla}
          initialWhatsapp={normalizeWhatsAppPhone(whatsapp, countryCode) ? `+${normalizeWhatsAppPhone(whatsapp, countryCode)}` : whatsapp}
          companyName={companyName}
          onFinish={handleWowFinish}
          saving={saving}
        />
      )}
    </div>
  );
}
